import React from "react";
import { useLanguage } from "../context/LanguageContext";
import { SITE } from "../lib/constants";

const Hero = () => {
  const { t } = useLanguage();

  const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 72;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative isolate overflow-hidden bg-[#0A192F] text-white pt-28 md:pt-36 pb-20 md:pb-32"
    >
      {/* Background layers */}
      <div className="pointer-events-none absolute inset-0 bg-grid-navy opacity-80" aria-hidden="true"></div>
      <div
        className="pointer-events-none absolute -top-32 -left-24 h-96 w-96 rounded-full bg-[#FF5A1F]/15 blur-[120px]"
        aria-hidden="true"
      ></div>
      <div
        className="pointer-events-none absolute bottom-0 right-0 h-[28rem] w-[28rem] rounded-full bg-[#1a3a6e]/40 blur-[140px]"
        aria-hidden="true"
      ></div>

      <div className="container-page relative grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        {/* Left : content */}
        <div className="lg:col-span-7">
          <div className="chip-light mb-6" data-testid="hero-eyebrow">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#FF5A1F]"></span>
            {t.hero.eyebrow}
          </div>

          <h1
            data-testid="hero-title"
            className="font-display font-black tracking-tight leading-[0.95] text-[2.75rem] sm:text-6xl lg:text-7xl xl:text-[5.5rem]"
          >
            <span className="block text-white/90">{t.hero.titleLine1}</span>
            <span className="block">
              {t.hero.titleLine2}
              <span className="inline-block h-3 w-3 align-super ml-2 rounded-full bg-[#FF5A1F]"></span>
            </span>
          </h1>

          <p
            data-testid="hero-subtitle"
            className="mt-6 max-w-xl text-base md:text-lg text-white/75 leading-relaxed"
          >
            {t.hero.subtitle}
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <button
              type="button"
              data-testid="hero-cta-primary"
              onClick={() => scrollToId("contact")}
              className="btn-primary"
            >
              <i className="fa-solid fa-calendar-check" aria-hidden="true"></i>
              {t.hero.ctaPrimary}
            </button>
            <button
              type="button"
              data-testid="hero-cta-secondary"
              onClick={() => scrollToId("about")}
              className="btn-ghost-light"
            >
              <i className="fa-solid fa-arrow-down" aria-hidden="true"></i>
              {t.hero.ctaSecondary}
            </button>
          </div>

          {/* Trust row */}
          <p
            data-testid="hero-trust"
            className="mt-10 text-[11px] sm:text-xs uppercase font-semibold tracking-[0.18em] text-white/55"
          >
            {t.hero.trustRow}
          </p>

          {/* Stats */}
          <dl className="mt-8 grid grid-cols-3 gap-4 max-w-lg">
            {[
              { v: "25+", k: t.hero.statExperience },
              { v: "8000+", k: t.hero.statInterventions },
              { v: "40+", k: t.hero.statPublications },
            ].map((s, i) => (
              <div
                key={i}
                data-testid={`hero-stat-${i}`}
                className="rounded-xl border border-white/10 bg-white/5 p-4 backdrop-blur-sm"
              >
                <dt className="font-display text-2xl md:text-3xl font-extrabold text-[#FF5A1F]">
                  {s.v}
                </dt>
                <dd className="mt-1 text-[11px] uppercase tracking-[0.14em] text-white/70 leading-tight">
                  {s.k}
                </dd>
              </div>
            ))}
          </dl>
        </div>

        {/* Right : visual */}
        <div className="lg:col-span-5 relative">
          <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl bg-[#0A192F]">
            <img
              src={SITE.sportImages[0]}
              alt="Sportif - traumatologie de l'épaule chez le rugbyman, prise en charge à Marseille par le Dr Aswad"
              className="w-full h-auto max-h-[65vh] object-contain object-center md:max-h-none md:h-[520px] md:object-cover"
              loading="eager"
              width="1600"
              height="1000"
            />
            {/* Orange accent strip */}
            <div className="absolute inset-x-0 bottom-0 h-1.5 bg-[#FF5A1F]"></div>
          </div>

          {/* Floating IMOTEpS chip card */}
          <div className="hidden md:flex absolute -left-6 bottom-8 items-center gap-3 rounded-xl bg-white p-3 pr-5 shadow-2xl shadow-[#0A192F]/40">
            <img
              src={SITE.logo}
              alt=""
              aria-hidden="true"
              className="h-12 w-12 object-contain"
            />
            <div className="text-left">
              <p className="font-display font-bold text-navy text-sm leading-tight">
                IMOTEpS
              </p>
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                Marseille
              </p>
            </div>
          </div>

          {/* Floating expertise chip */}
          <div className="hidden md:flex absolute -right-4 top-8 flex-col rounded-xl border border-white/10 bg-[#112240] p-4 pr-6 shadow-2xl">
            <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#FF5A1F]">
              Expert HAS
            </span>
            <span className="mt-1 font-display font-bold text-white text-sm">
              Prothèses d'épaule
            </span>
          </div>
        </div>
      </div>

      {/* Marquee of affiliations */}
      <div className="container-page mt-16 md:mt-20">
        <div className="overflow-hidden border-y border-white/10 py-5">
          <div className="flex gap-10 whitespace-nowrap animate-marquee will-change-transform">
            {[
              "SOFCOT",
              "SOFEC",
              "SFA",
              "SECEC",
              "Mayo Clinic",
              "University of Toronto",
              "HAS",
              "CARMF",
              "SOFCOT",
              "SOFEC",
              "SFA",
              "SECEC",
              "Mayo Clinic",
              "University of Toronto",
              "HAS",
              "CARMF",
            ].map((label, i) => (
              <span
                key={i}
                className="font-display text-sm md:text-base font-bold uppercase tracking-[0.22em] text-white/40"
              >
                {label}
                <span className="mx-5 text-[#FF5A1F]">·</span>
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
